import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { useFormHandler } from "shared/hooks";
import { phoneNumberRegex } from "widgets/booker/shared/constants";
import { useCartContext } from "widgets/booker/contexts";
import i18n, { i18nLanguage, i18nNamespace, loadLanguagePacks } from "i18n";
import { useTranslation } from "react-i18next";

// Ensure the required language packs are loaded
await loadLanguagePacks(
  [i18nNamespace.Core, i18nNamespace.Booker],
  "booker",
  i18n.language as i18nLanguage,
);

const getCustomerDetailsFormSchema = (phoneRequired = true) =>
  z.object({
    firstName: z
      .string({
        required_error: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    lastName: z
      .string({
        required_error: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    email: z
      .string({
        required_error: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .email({
        message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL"),
      }),
    phone: phoneRequired
      ? z
          .string({
            required_error: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.PHONE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          })
          .min(1, {
            message: `${i18n.t("booker:CUSTOMER_DETAILS.FORM.PHONE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          })
          .regex(phoneNumberRegex, {
            message: `${i18n.t("core:FORM_ERRORS.INVALID")} ${i18n.t("booker:CUSTOMER_DETAILS.FORM.PHONE")}`,
          })
      : z.string().optional(),
  });

export type CustomerDetailsFormValues = z.infer<
  ReturnType<typeof getCustomerDetailsFormSchema>
>;

export default function useCustomerDetailsForm(phoneRequired = true) {
  const { updateCustomer, customer } = useCartContext();
  const defaultValues: CustomerDetailsFormValues = {
    firstName: customer.firstName || "",
    lastName: customer.lastName || "",
    email: customer.email || "",
    phone: customer.phone || "",
  };

  const { i18n } = useTranslation();
  const [schema, setSchema] = useState<
    ReturnType<typeof getCustomerDetailsFormSchema>
  >(getCustomerDetailsFormSchema(phoneRequired));

  useEffect(() => {
    const loadLanguagePacksAndUpdateSchemas = async () => {
      await loadLanguagePacks(
        [i18nNamespace.Core, i18nNamespace.Booker],
        "booker",
        i18n.language as i18nLanguage,
      );
      setSchema(getCustomerDetailsFormSchema(phoneRequired));
    };
    loadLanguagePacksAndUpdateSchemas();
  }, [i18n.language, phoneRequired, setSchema]);

  return useFormHandler<CustomerDetailsFormValues>(
    useCallback(
      async data => {
        const payload = { ...data };
        const { email, firstName, lastName } = payload;
        const phone = payload.phone ?? "";

        if (payload && email && firstName && lastName && (phone || !phoneRequired)) {
          updateCustomer({ email, firstName, lastName, phone, complete: true });
        }
      },
      [updateCustomer, phoneRequired],
    ),
    defaultValues,
    { schema },
    { shouldFocusError: false, mode: "onChange" },
  );
}
